import get from 'lodash/get';
import moment from 'moment';

import {useMediaQuery} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import Grid from "@mui/material/Grid";
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from "@mui/material/Typography";
import Stack from '@mui/material/Stack';

import {formatSessionDate} from '../../helpers/sessions';
import DownloadInvoiceButton from '../sessions/DownloadInvoiceButton';
import EditCreditCard from './EditCreditCard';
import config from "../../config/config";
import SportsKabaddiIcon from "@mui/icons-material/SportsKabaddi";
import BlankContent from "../common/BlankContent";
import CustomPagination from "../common/CustomPagination";


const PatientPaymentCard = props => {
  const {session: s} = props;
  if (!s) {
    return <></>;
  }
  return (
    <Stack key={s.id} borderTop={`1px solid ${config().colors.border}`}>
      <Stack direction={{xs: 'column', xsm: 'row'}} justifyContent={"space-between"}>
        <Stack spacing={1} paddingX={{xs: 1, xsm: 2}} paddingY={2}>
          <Stack direction='row' spacing={{xs: 1, xsm: 2}}>
            <EventAvailableIcon sx={{color: config().colors.text}} fontSize='small'/>
            <Typography noWrap={true}>
              <Box component="span" sx={{display: {xs: 'inline', xsm: 'none'}}}>Service:&nbsp;</Box>
              <Box component="span" sx={{display: {xs: 'none', xsm: 'inline'}}}>Date of Service:&nbsp;</Box>
              <span>{formatSessionDate(s)}</span>
            </Typography>
          </Stack>
          <Stack direction='row' spacing={{xs: 1, xsm: 2}}>
            <EventAvailableIcon sx={{color: config().colors.text}} fontSize='small'/>
            <Typography noWrap={true}>
              <Box component="span" sx={{display: {xs: 'inline', xsm: 'none'}}}>Charge:&nbsp;</Box>
              <Box component="span" sx={{display: {xs: 'none', xsm: 'inline'}}}>Date of Charge:&nbsp;</Box>
              <span>{
                s.date_completed
                  ? moment(s.date_completed).format('M/D/YY')
                  : ''
              }</span>
            </Typography>
          </Stack>
          <Stack direction='row' spacing={{xs: 1, xsm: 2}}>
            <CreditCardIcon sx={{color: config().colors.text}} fontSize='small'/>
            <Typography noWrap={true}>
              <span>Amount:&nbsp;</span>
              <span>${s.total}</span>
            </Typography>
          </Stack>
          <Stack sx={{display: {xs: 'block', xsm: 'none'}}}>
            <DownloadInvoiceButton fullWidth={true} variant='outlined' textOnly={true} session={s}/>
          </Stack>
        </Stack>
        <Stack justifyContent="center" sx={{display: {xs: 'none', xsm: 'flex'}}}>
          <DownloadInvoiceButton iconOnly={true} textOnly={true} session={s}/>
        </Stack>
      </Stack>
    </Stack>
  );
};

const PatientPaymentScreen = props => {
  const isLgScreen = useMediaQuery(theme => theme.breakpoints.down('lg'));
  return (
    <Box>
      <Stack spacing={2} padding={2}>
        <EditCreditCard/>
        <Paper
          sx={{
            boxShadow: config().styles.defaultBoxShadow,
            borderRadius: config().styles.defaultRadius,
            paddingX: {xs: 1, sm: 3},
            paddingY: 1
          }}
        >
          <TableContainer sx={{display: {xs: 'none', sm: 'block'}}}>
            <Table className="custom-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{paddingX: '10px'}} width={isLgScreen ? 'auto' : '27%'} align="left">Date of
                    Service</TableCell>
                  <TableCell sx={{paddingX: '10px'}} width={isLgScreen ? 'auto' : '27%'} align="left">Date of
                    Charge</TableCell>
                  <TableCell sx={{paddingX: '10px'}} width={isLgScreen ? 'auto' : '27%'} align="left">Amount</TableCell>
                  <TableCell sx={{paddingX: '10px'}} width={isLgScreen ? 'auto' : '27%'} align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(props.sessions || []).map(s =>
                  <TableRow
                    key={get(s, 'id')}
                    sx={{'&:last-child td, &:last-child th': {border: 0}}}
                  >
                    <TableCell>
                      {formatSessionDate(s)}
                    </TableCell>
                    <TableCell>
                      {
                        s.date_completed
                          ? moment(s.date_completed).format('M/D/YY')
                          : ''
                      }
                    </TableCell>
                    <TableCell>
                      ${s.total}
                    </TableCell>
                    <TableCell>
                      <DownloadInvoiceButton iconOnly={isLgScreen} textOnly={true}
                                             variant='outlined' session={s}/>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {(props.sessions || []).length === 0 ? <BlankContent
              title='No completed sessions found!'
              Icon={SportsKabaddiIcon}
            /> : null}
            <Box marginY='1em'>
              {(props.sessions || []).length > 0
                ?
                <CustomPagination {...props}/>
                : null
              }
            </Box>
          </TableContainer>
          <Box sx={{display: {'xs': 'block', 'sm': 'none', 'md': 'none', 'lg': 'none'}}}>
            {(props.sessions || []).length > 0 &&
              <Grid container columnSpacing={{xs: 2}} paddingX={1}
                    sx={{alignItems: "center", width: "100%"}}>
                <Grid item sx={{display: 'flex'}}>
                  <Avatar src={"/images/logo-180px.png"} sx={{width: '42px', height: '42px'}}/>
                </Grid>
                <Grid item xs>
                  <Typography variant="normal">Payments</Typography>
                </Grid>
                <Grid item xs="auto">
                  <Typography
                    variant="largeSemiBoldTight">{(props.sessions || []).length}</Typography>
                </Grid>
              </Grid>
            }
            {(props.sessions || []).length > 0 &&
              <>
                <Stack paddingY={1}>
                  {(props.sessions || []).map(row =>
                    <PatientPaymentCard session={row}/>
                  )}
                </Stack>
                <Box marginY='1em'>
                  <CustomPagination 
                    {...props}
                  />
                </Box>
              </>

            }
            {(props.sessions || []).length === 0 ? <BlankContent
              title='No completed sessions found!'
              Icon={SportsKabaddiIcon}
            /> : null}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export default PatientPaymentScreen;
